import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import NotFoundOrRedirect from './components/layout/NotFoundOrRedirect';
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import MarketTicker from "@/components/MarketTicker";
import CustomCursor from "@/components/CustomCursor";
import LoadingScreen from "@/components/LoadingScreen";
import { GlobalErrorBoundary } from "@/components/ErrorBoundary";

// Suppress cross-origin "Script error." from third-party widgets (e.g. TradingView)
if (typeof window !== "undefined") {
  const _origOnError = window.onerror;
  window.onerror = function(msg, src, line, col, err) {
    if (msg === "Script error." || msg === "Script error") return true;
    if (_origOnError) return _origOnError(msg, src, line, col, err);
    return false;
  };
}

// Pages
import LandingPage from "@/pages/LandingPage";
import StudentDashboard from "@/pages/StudentDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAlertsPage from "@/pages/AdminAlertsPage";
import MentorDashboard from "@/pages/MentorDashboard";
import CounselorDashboard from "@/pages/CounselorDashboard";
import TechDashboard from "@/pages/TechDashboard";
import CoursePage from "@/pages/CoursePage";
import LessonPage from "@/pages/LessonPage";
import LearningMaterialsPage from "@/pages/LearningMaterialsPage";
import MessagesPage from "@/pages/MessagesPage";
import ReferralsPage from "@/pages/ReferralsPage";
import PaymentsPage from "@/pages/PaymentsPage";
import SupportPage from "@/pages/SupportPage";
import TradingDashboard from './pages/TradingDashboard';
import ProfilePage from "@/pages/ProfilePage";
import QuizPage from "@/pages/QuizPage";
import CertificatesPage from "@/pages/CertificatesPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import TradingJournalPage from "@/pages/TradingJournalPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import DrishtiAlertsPage from "@/pages/DrishtiAlertsPage";
import DrishtiLandingPage from "@/pages/DrishtiLandingPage";
import LiveClassesPage from "@/pages/LiveClassesPage";
import LegalPage from "@/pages/LegalPage";
import GalleryPage from "@/pages/GalleryPage";
import DoubtCentrePage from "@/pages/DoubtCentrePage";
import DhanPortfolioPage from "@/pages/DhanPortfolioPage";
import MentorCRMPage from "@/pages/MentorCRMPage";
import DynamicPage from "@/pages/DynamicPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import ContactLeadsPage from "@/pages/ContactLeadsPage";
import MarketBriefPage from "@/pages/MarketBriefPage";
import TradingCalculatorPage from "@/pages/TradingCalculatorPage";
import SiteSeoDashboard from "@/pages/SiteSeoDashboard";
import LiveBatchesPage from "@/pages/LiveBatchesPage";
import AdminBatchesPage from "@/pages/AdminBatchesPage";
import ManagementDashboard from "@/pages/ManagementDashboard";

import CityLandingPage from "@/pages/CityLandingPage";
import WelcomeAttendancePage from "@/pages/WelcomeAttendancePage";
import AdminTeamManagementPage from "@/pages/AdminTeamManagementPage";
import { Helmet } from "react-helmet-async";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const API = `${BACKEND_URL}/api`;
const AUTH_TOKEN_KEY = "jamadhan_session_token";
const AUTH_USER_KEY = "jamadhan_user_data";

const getStoredAuthToken = () => {
  try {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
};

const storeAuthToken = (token) => {
  if (!token) return;
  try {
    window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // Ignore storage failures; secure cookies still cover browsers that allow them.
  }
};

const clearAuthToken = () => {
  try {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const getStoredUser = () => {
  try {
    const raw = window.localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeUser = (u) => {
  if (!u) return;
  try {
    window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(u));
  } catch {}
};

const clearStoredUser = () => {
  try {
    window.localStorage.removeItem(AUTH_USER_KEY);
  } catch {}
};

const stripAuthToken = (candidate) => {
  if (!candidate || typeof candidate !== "object") return candidate;
  const { session_token, ...user } = candidate;
  return user;
};

// Set a global default timeout (10 seconds) so API calls never hang the UI forever.
axios.defaults.timeout = 10000;

axios.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    if (status === 401) {
      // Only clear stored session token when the core /auth/me check explicitly reports 401 unauthorized.
      // Transient or sub-resource 401s (e.g. broker feeds, external tickers) must NEVER blow away active admin sessions.
      if (url.includes("/auth/me") || url.includes("/auth/logout")) {
        clearAuthToken();
        clearStoredUser();
      }
    }
    // 503/504 = backend is temporarily down — show a non-invasive toast so the page doesn't hang blank
    if (status === 503 || status === 504 || error.code === "ECONNABORTED") {
      // Only show once per 30s to avoid toast spam
      const now = Date.now();
      const lastToast = window.__lastBackendDownToast || 0;
      if (now - lastToast > 30000) {
        window.__lastBackendDownToast = now;
        toast.warning("Server is temporarily unreachable. Retrying…", { duration: 4000 });
      }
    }
    return Promise.reject(error);
  }
);

const isValidUser = (candidate) => (
  Boolean(
    candidate &&
    typeof candidate === "object" &&
    candidate.user_id &&
    candidate.role &&
    (candidate.name || candidate.email || candidate.phone)
  )
);

const getDashboardPath = (role) => {
  if (role === "admin") return "/admin";
  if (role === "management") return "/management";
  if (role === "mentor") return "/mentor";
  if (role === "counselor") return "/counselor";
  if (role === "tech") return "/tech";
  return "/dashboard";
};

const getLogoutRedirectUrl = (nextPath = "/") => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const next = origin ? `${origin}${nextPath}` : "/";
  return `${API}/auth/logout/redirect?next=${encodeURIComponent(next)}`;
};

// Theme Context
export const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    return typeof window !== "undefined" ? localStorage.getItem("jamadhan_theme") || "dark" : "dark";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("jamadhan_theme", theme);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const cached = getStoredUser();
    return isValidUser(cached) && getStoredAuthToken() ? cached : null;
  });
  const [loading, setLoading] = useState(() => !getStoredUser());

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        timeout: 6000
      });
      const authUser = stripAuthToken(response.data);
      if (isValidUser(authUser)) {
        setUser(authUser);
        storeUser(authUser);
      } else {
        setUser(null);
        clearStoredUser();
      }
    } catch (error) {
      // Avoid clearing a freshly established token session because an older
      // startup /me request returned late with 401.
      if (!getStoredAuthToken()) {
        setUser(null);
        clearStoredUser();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();

    // Real-time access sync: polls every 3.5s and checks on tab focus so admin approve/suspend/revoke reflects instantly without refresh
    const pollTimer = setInterval(() => {
      if (getStoredAuthToken() && !document.hidden) {
        checkAuth();
      }
    }, 3500);

    const onVisibilityChange = () => {
      if (!document.hidden && getStoredAuthToken()) {
        checkAuth();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [checkAuth]);

  const login = (redirectPath) => {
    const target = redirectPath || (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/dashboard");
    if (typeof window !== "undefined") {
      window.location.assign(`/login?redirect=${encodeURIComponent(target)}`);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, timeout: 5000 });
    } catch {
      // Continue local logout even if the network is unavailable.
    }
    clearAuthToken();
    clearStoredUser();
    setUser(null);
    setLoading(false);
    window.location.assign(getLogoutRedirectUrl());
  };

  const logoutUrl = "/logout";

  const adminLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: String(email || "").trim(),
        password: String(password || "").trim()
      }, { withCredentials: true, timeout: 15000 });
      storeAuthToken(response.data?.session_token);
      const authUser = stripAuthToken(response.data);
      if (!isValidUser(authUser)) {
        throw new Error("Invalid login response");
      }
      setUser(authUser);
      storeUser(authUser);
      setLoading(false);
      return authUser;
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const startSignup = async (payload) => {
    const response = await axios.post(`${API}/auth/signup/start`, payload, { withCredentials: true });
    return response.data;
  };

  const verifySignup = async (email, otp) => {
    const response = await axios.post(`${API}/auth/signup/verify`, { email, otp }, { withCredentials: true });
    return response.data;
  };

  const completeSignup = async (email, password, referral_code = null) => {
    try {
      const response = await axios.post(`${API}/auth/signup/complete`, { email, password, referral_code }, { withCredentials: true });
      storeAuthToken(response.data?.session_token);
      const authUser = stripAuthToken(response.data);
      if (!isValidUser(authUser)) {
        throw new Error("Invalid signup response");
      }
      setUser(authUser);
      storeUser(authUser);
      setLoading(false);
      return authUser;
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const requestPasswordReset = async (email) => {
    const response = await axios.post(`${API}/auth/password/forgot`, { email }, { withCredentials: true });
    return response.data;
  };

  const resetPassword = async (email, otp, password) => {
    const response = await axios.post(`${API}/auth/password/reset`, { email, otp, password }, { withCredentials: true });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, logoutUrl, adminLogin, startSignup, verifySignup, completeSignup, requestPasswordReset, resetPassword, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const pendingRefCode = localStorage.getItem("jamadhan_referral_code") || "";
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId, referral_code: pendingRefCode },
            { withCredentials: true }
          );
          
          if (pendingRefCode) {
            localStorage.removeItem("jamadhan_referral_code");
          }
          storeAuthToken(response.data?.session_token);
          const authUser = stripAuthToken(response.data);
          if (!isValidUser(authUser)) {
            throw new Error("Invalid auth response");
          }

          setUser(authUser);
          storeUser(authUser);
          toast.success(`Welcome, ${authUser.name || "there"}!`);
          
          // Navigate based on role
          navigate(getDashboardPath(authUser.role), { replace: true, state: { user: authUser } });
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Authentication failed");
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#94A3B8]">Authenticating...</p>
      </div>
    </div>
  );
};

const LogoutPage = () => {
  useEffect(() => {
    const runLogout = async () => {
      try {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      } catch {
        // Always clear the browser session even if API logout cannot finish.
      }
      clearAuthToken();
      window.location.replace("/");
    };
    runLogout();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#94A3B8]">Signing out...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isValidUser(user)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    return <Navigate to={getDashboardPath(user.role)} replace />;
  }

  // Wrap every protected page in an error boundary so crashes show a recovery
  // screen instead of a blank/black page.
  return <GlobalErrorBoundary>{children}</GlobalErrorBoundary>;
};


// Route Alias — redirects custom paths to canonical paths
const RouteAlias = ({ from, to }) => {
  const location = useLocation();
  if (location.pathname === from || location.pathname === from + "/") {
    return <Navigate to={to} replace />;
  }
  return null;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const [routeAliases, setRouteAliases] = useState([]);
  const [redirects, setRedirects] = useState([]);
  const [globalSchema, setGlobalSchema] = useState("");

  useEffect(() => {
    axios.get(`${API}/site-config`).then(res => {
      const routes = res.data?.page_routes;
      if (!routes) return;
      const defaults = {
        blog: "/blog", drishti: "/drishti", journal: "/journal",
        leaderboard: "/leaderboard", recordings: "/recordings",
        certificates: "/certificates", referrals: "/referrals",
        payments: "/payments", support: "/support"
      };
      const aliases = [];
      for (const [key, customPath] of Object.entries(routes)) {
        const canonical = defaults[key];
        if (canonical && customPath !== canonical && customPath.startsWith("/")) {
          aliases.push({ from: customPath, to: canonical });
        }
      }
      setRouteAliases(aliases);
      if (res.data?.global_schema) {
        setGlobalSchema(res.data.global_schema);
      }
    }).catch(() => {});

    axios.get(`${API}/redirects`).then(res => {
      setRedirects(res.data || []);
    }).catch(() => {});
  }, []);
  
  // Check redirects dynamically
  const currentPath = location.pathname;
  const matchRedirect = redirects.find(r => r.old_url === currentPath || r.old_url === currentPath + '/');
  if (matchRedirect) {
    if (matchRedirect.new_url.startsWith("http")) {
      window.location.href = matchRedirect.new_url;
      return null;
    }
    return <Navigate to={matchRedirect.new_url} replace />;
  }
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      {globalSchema && (
        <Helmet>
          <script type="application/ld+json">{globalSchema}</script>
        </Helmet>
      )}
      <Routes>
        <Route path="/" element={<LandingPage />} />
      
      {/* Student Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/course/:courseId" element={
        <ProtectedRoute>
          <CoursePage />
        </ProtectedRoute>
      } />
      <Route path="/lesson/:lessonId" element={
        <ProtectedRoute>
          <LessonPage />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/learning-materials" element={
        <ProtectedRoute>
          <LearningMaterialsPage />
        </ProtectedRoute>
      } />
      <Route path="/recordings" element={<Navigate to="/dashboard#recorded-courses" replace />} />
      <Route path="/notes" element={<Navigate to="/dashboard#recorded-courses" replace />} />
      <Route path="/recorded-courses" element={<Navigate to="/dashboard#recorded-courses" replace />} />
      <Route path="/referrals" element={
        <ProtectedRoute>
          <ReferralsPage />
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute>
          <PaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/drishti" element={
        <ProtectedRoute>
          <DrishtiAlertsPage />
        </ProtectedRoute>
      } />
      <Route path="/broker" element={
        <ProtectedRoute>
          <DhanPortfolioPage />
        </ProtectedRoute>
      } />
      <Route path="/quizzes" element={
        <ProtectedRoute>
          <QuizPage />
        </ProtectedRoute>
      } />
      <Route path="/certificates" element={
        <ProtectedRoute>
          <CertificatesPage />
        </ProtectedRoute>
      } />
      <Route path="/journal" element={
        <ProtectedRoute>
          <TradingJournalPage />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/doubts" element={
        <ProtectedRoute>
          <DoubtCentrePage />
        </ProtectedRoute>
      } />
      <Route path="/briefs" element={
        <ProtectedRoute>
          <MarketBriefPage />
        </ProtectedRoute>
      } />
      <Route path="/calculator" element={
        <ProtectedRoute>
          <TradingCalculatorPage />
        </ProtectedRoute>
      } />
      <Route path="/batches" element={
        <ProtectedRoute>
          <LiveBatchesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/batches" element={
        <ProtectedRoute allowedRoles={['admin', 'management']}>
          <AdminBatchesPage />
        </ProtectedRoute>
      } />
      
      {/* Management Route */}
      <Route path="/management" element={
        <ProtectedRoute allowedRoles={['admin', 'management']}>
          <ManagementDashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/referrals" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Navigate to="/admin?tab=referrals" replace />
        </ProtectedRoute>
      } />
      <Route path="/admin/alerts" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAlertsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <ProtectedRoute allowedRoles={['admin', 'mentor', 'counselor', 'tech']}>
          <ContactLeadsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/team" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminTeamManagementPage />
        </ProtectedRoute>
      } />

      {/* Admin & Tech Site SEO Suite */}
      <Route path="/admin/site-seo" element={
        <ProtectedRoute allowedRoles={['admin', 'tech']}>
          <SiteSeoDashboard />
        </ProtectedRoute>
      } />

      {/* Tech Routes */}
      <Route path="/tech" element={
        <ProtectedRoute allowedRoles={['tech', 'admin']}>
          <SiteSeoDashboard />
        </ProtectedRoute>
      } />

      
      {/* Mentor Routes */}
      <Route path="/mentor-dashboard" element={
        <ProtectedRoute allowedRoles={['mentor', 'admin']}>
          <MentorCRMPage />
        </ProtectedRoute>
      } />
      <Route path="/mentor" element={
        <ProtectedRoute allowedRoles={['mentor', 'admin']}>
          <MentorDashboard />
        </ProtectedRoute>
      } />
      
      {/* Counselor Routes */}
      <Route path="/counselor" element={
        <ProtectedRoute allowedRoles={['mentor', 'admin', 'counselor', 'tech']}>
          <CounselorDashboard />
        </ProtectedRoute>
      } />
      
      {/* Fallback & Marketing Routes */}
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogDetailPage />} />
      <Route path="/blog/:slug/" element={<BlogDetailPage />} />
      <Route path="/admin-login" element={<AdminLoginPage />} />
      <Route path="/login" element={<AdminLoginPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/drishti-ai" element={<DrishtiLandingPage />} />
      <Route path="/drishti-ai/" element={<DrishtiLandingPage />} />
      <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
      <Route path="/privacy-policy/" element={<LegalPage type="privacy" />} />
      <Route path="/privacy" element={<Navigate to="/privacy-policy/" replace />} />
      <Route path="/terms-and-conditions" element={<LegalPage type="terms" />} />
      <Route path="/terms-and-conditions/" element={<LegalPage type="terms" />} />
      <Route path="/terms" element={<Navigate to="/terms-and-conditions/" replace />} />

      {/* Canonical Refund & Cancellation Policy */}
      <Route path="/refund-and-cancellation-policy" element={<LegalPage type="refunds" />} />
      <Route path="/refund-and-cancellation-policy/" element={<LegalPage type="refunds" />} />
      <Route path="/refund-and-cancellation" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />
      <Route path="/refund-and-cancellation/" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />
      <Route path="/refund" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />
      <Route path="/refund/" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />
      <Route path="/refund-policy" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />
      <Route path="/refund-policy/" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />
      <Route path="/refunds" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />
      <Route path="/refunds/" element={<Navigate to="/refund-and-cancellation-policy/" replace />} />

      <Route path="/drishti-alerts" element={<Navigate to="/drishti" replace />} />
      <Route path="/live-classes" element={
        <ProtectedRoute>
          <LiveClassesPage />
        </ProtectedRoute>
      } />
      <Route path="/courses" element={<Navigate to="/#courses" replace />} />
      <Route path="/courses/" element={<Navigate to="/#courses" replace />} />
      <Route path="/courses/:courseId" element={<CourseDetailPage />} />
      <Route path="/courses/:courseId/" element={<CourseDetailPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/gallery/" element={<GalleryPage />} />
      {/* /about uses the original static page; Tech Team can override via Pages CMS once content is saved */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/about/" element={<AboutPage />} />
      <Route path="/faq" element={<DynamicPage slug="faq" />} />
      <Route path="/faq/" element={<DynamicPage slug="faq" />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/contact/" element={<ContactPage />} />
      <Route path="/welcome" element={<WelcomeAttendancePage />} />

      {/* City Landing Pages (Rajasthan) */}
      <Route path="/city/:citySlug" element={<CityLandingPage />} />
      <Route path="/stock-market-course-in-jaipur" element={<CityLandingPage />} />
      <Route path="/stock-market-course-in-jaipur/" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-jodhpur" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-jodhpur/" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-udaipur" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-udaipur/" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-kota" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-kota/" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-ajmer" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-ajmer/" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-sikar" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-sikar/" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-bhilwara" element={<CityLandingPage />} />
      <Route path="/stock-market-classes-in-bhilwara/" element={<CityLandingPage />} />

      {/* Route aliases from admin config */}
      {routeAliases.map(alias => (
        <Route key={alias.from} path={alias.from} element={<Navigate to={alias.to} replace />} />
      ))}

      <Route path="*" element={<NotFoundOrRedirect />} />
    </Routes>
    </>
  );
}

function TrailingSlashEnforcer() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { pathname, search, hash } = location;
    if (!pathname || pathname === "/") return;
    
    // Ignore static files/assets with extensions (e.g. .png, .jpg, .svg, .json)
    const segments = pathname.split("/");
    const last = segments[segments.length - 1];
    if (last && last.includes(".")) return;

    if (!pathname.endsWith("/")) {
      navigate(`${pathname}/${search || ""}${hash || ""}`, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}

const PUBLIC_ROUTES = [
  '/', '/about', '/contact', '/gallery', '/faq', '/blog',
  '/privacy-policy', '/privacy', '/terms-and-conditions', '/terms',
  '/refund-and-cancellation', '/refund', '/refund-policy', '/refunds',
  '/refund-and-cancellation-policy',
  '/drishti-ai', '/admin-login', '/login', '/logout',
  '/courses',
  '/city',
  '/stock-market-course-in-jaipur',
  '/stock-market-classes-in-jodhpur',
  '/stock-market-classes-in-udaipur',
  '/stock-market-classes-in-kota',
  '/stock-market-classes-in-ajmer',
  '/stock-market-classes-in-sikar',
  '/stock-market-classes-in-bhilwara'
];

const isPublicPath = (pathname) => {
  const clean = (pathname || "/").replace(/\/+$/, "") || "/";
  if (clean === '/') return true;
  if (clean.startsWith('/courses/')) {
    return !clean.startsWith('/courses/watch');
  }
  return PUBLIC_ROUTES.some(p => clean === p || clean.startsWith(p + '/'));
};

function AppContent({ showLoading, setShowLoading }) {
  const location = useLocation();
  const { theme } = useTheme();
  const isLandingPage = location.pathname === "/";
  const isDashboard = !isPublicPath(location.pathname);

  // Automatically scroll to top on every route change (unless navigating to an in-page anchor)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Force dark mode on all public marketing pages. Light mode on all dashboard / app routes.
      if (isDashboard && theme === "light") {
        document.documentElement.classList.add("light");
        document.documentElement.classList.remove("dark");
      } else {
        document.documentElement.classList.add("dark");
        document.documentElement.classList.remove("light");
      }
    }
  }, [location.pathname, theme, isDashboard]);

  return (
    <div className={`App min-h-screen bg-transparent transition-colors duration-500 ${isLandingPage ? 'app-with-market-ticker' : ''}`}>
      <TrailingSlashEnforcer />
      {showLoading && <LoadingScreen onComplete={() => setShowLoading(false)} />}
      <CustomCursor />
      {isLandingPage && (
        <GlobalErrorBoundary fallback={null}>
          <MarketTicker />
        </GlobalErrorBoundary>
      )}
      <AppRouter />
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#131B2C',
            border: '1px solid #1E293B',
            color: '#F8FAFC'
          }
        }}
      />
    </div>
  );
}

function App() {
  const [showLoading, setShowLoading] = useState(true);

  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        console.log("Seed skipped:", error.response?.data?.message || "already seeded"); // eslint-disable-line no-console
      }
    };
    seedData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture referral code from URL
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const refCode = params.get("ref");
      if (refCode) {
        localStorage.setItem("jamadhan_referral_code", refCode);
        sessionStorage.setItem("jamadhan_referral_code", refCode);
      }
    } catch (_) {}
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppContent showLoading={showLoading} setShowLoading={setShowLoading} />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;

// Export API for use in other components
export { API, BACKEND_URL, isValidUser };
